<template>
  <div class="charge-page">
    <!-- 页头 -->
    <section class="page-hero">
      <div class="container">
        <h1>充值中心</h1>
        <p>充值余额可用于全部标准接口的按次调用，多档金额享赠送优惠。价格与赠送规则无需登录即可查看，实际支付需登录后进行。</p>
      </div>
    </section>

    <section class="charge-band">
      <div class="container">
        <!-- 企业认证状态条（状态 1 / 2 / 3 / 5，判定与文案沿用旧版） -->
        <div class="acct-strip warn" v-if="state===1">
          <span class="acct-dot"></span>
          <span>您当前未进行企业认证，暂时无法充值</span>
          <a class="acct-login" href="javascript:;" @click="goOrg">立即认证</a>
        </div>
        <div class="acct-strip warn" v-if="state===2">
          <span class="acct-dot"></span>
          <span>您已提交企业认证信息，正在审核中，请等待...</span>
        </div>
        <div class="acct-strip warn" v-if="state===3">
          <span class="acct-dot"></span>
          <span>您提交的企业认证信息，审核未通过</span>
          <a class="acct-login" href="javascript:;" @click="goOrg">立即查看</a>
        </div>
        <div class="acct-strip warn" v-if="state===5">
          <span class="acct-dot"></span>
          <span>当前认证已到期，请重新认证后再进行充值调用，即将为您跳转到认证页面</span>
        </div>

        <!-- 账户状态条：未登录时价格与规则同样可见，只有支付需要登录 -->
        <div class="acct-strip on" v-if="logged">
          <span class="acct-dot"></span>
          <span>已登录：<b>{{ userName }}</b></span>
          <span class="acct-bal" v-if="balanceKnown">当前余额 <b>¥{{ balanceText }}</b></span>
          <router-link class="acct-login" to="/center/case1">查看余额与订单</router-link>
        </div>
        <div class="acct-strip off" v-else>
          <span class="acct-dot"></span>
          <span>当前未登录：<b>价格与赠送规则可直接查看</b>，实际支付需登录后进行。</span>
          <a class="acct-login" href="javascript:;" @click="openAuth('login')">立即登录 / 注册</a>
        </div>

        <div class="charge-card">
          <!-- 充值订单：认证通过（state===4）时显示；类型锁定为「充值调用」，只能关联充值订单 -->
          <div class="charge-row" v-if="state===4">
            <div class="charge-label">充值订单：</div>
            <div class="charge-body">
              <secret :only-mode="1" @orderId="getOrderId"></secret>
              <p class="charge-hint">
                充值需关联一笔「充值调用」订单（充值后余额按次扣费）；包年 / 包天套餐请在数据产品页选购。
              </p>
            </div>
          </div>

          <!-- 充值金额 -->
          <div class="charge-row">
            <div class="charge-label">充值金额：</div>
            <div class="charge-body">
              <div class="plans">
                <button
                  v-for="plan in plans"
                  :key="plan.amount"
                  class="plan"
                  type="button"
                  :class="{ active: isPlanActive(plan) }"
                  @click="selectPlan(plan)"
                >
                  <span class="plan-tag" :class="{ hot: plan.hot }">{{ plan.tag }}</span>
                  <span class="plan-amt">{{ plan.amount }} 元</span>
                  <span class="plan-bonus">充 {{ plan.amount }} 元，送 {{ giveMoneyFor(plan.amount) }} 元</span>
                </button>
              </div>
              <div class="plan-custom">
                <input
                  type="number"
                  v-model="form.money"
                  @focus="inputMoney"
                  placeholder="自定义金额"
                  autocomplete="off"
                >
                <span class="unit">元</span>
                <span class="tip">最低自定义充值金额为 100 元且须为整数；自定义金额不参与赠送活动</span>
              </div>
            </div>
          </div>

          <!-- 赠送金额 -->
          <div class="charge-row">
            <div class="charge-label"></div>
            <div class="charge-body">
              <div class="gift-line">赠送金额：<b>{{ giveAmount || 0 }}</b> 元
                <span class="gift-hint">{{ giftHint }}</span>
              </div>
            </div>
          </div>

          <!-- 支付方式 -->
          <div class="charge-row">
            <div class="charge-label">支付方式：</div>
            <div class="charge-body">
              <div class="pms">
                <button class="pm" type="button" :class="{ active: form.type===2 }" @click="form.type=2">
                  <span class="pm-icon pm-wx" aria-hidden="true">
                    <!-- WeChat 官方品牌矢量：Simple Icons（simple-icons/icons/wechat.svg），CC0 -->
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/></svg>
                  </span>微信支付
                </button>
                <button class="pm" type="button" :class="{ active: form.type===1 }" @click="form.type=1">
                  <span class="pm-icon pm-ali" aria-hidden="true">
                    <!-- 支付宝官方品牌矢量：Simple Icons（simple-icons/icons/alipay.svg），CC0 -->
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M19.695 15.07c3.426 1.158 4.203 1.22 4.203 1.22V3.846c0-2.124-1.705-3.845-3.81-3.845H3.914C1.808.001.102 1.722.102 3.846v16.31c0 2.123 1.706 3.845 3.813 3.845h16.173c2.105 0 3.81-1.722 3.81-3.845v-.157s-6.19-2.602-9.315-4.119c-2.096 2.602-4.8 4.181-7.607 4.181-4.75 0-6.361-4.19-4.112-6.949.49-.602 1.324-1.175 2.617-1.497 2.025-.502 5.247.313 8.266 1.317a16.796 16.796 0 0 0 1.341-3.302H5.781v-.952h4.799V6.975H4.77v-.953h5.81V3.591s0-.409.411-.409h2.347v2.84h5.744v.951h-5.744v1.704h4.69a19.453 19.453 0 0 1-1.986 5.06c1.424.52 2.702 1.011 3.654 1.333m-13.81-2.032c-.596.06-1.71.325-2.321.869-1.83 1.608-.735 4.55 2.968 4.55 2.151 0 4.301-1.388 5.99-3.61-2.403-1.182-4.438-2.028-6.637-1.809"/></svg>
                  </span>支付宝
                </button>
              </div>
            </div>
          </div>

          <!-- 协议 -->
          <div class="charge-row">
            <div class="charge-label"></div>
            <div class="charge-body">
              <label class="agree">
                <input type="checkbox" v-model="checked">
                <span>我已阅读并同意
                  <router-link target="_blank" to="/aboutUs/case2">《用户协议》</router-link>
                  与《充值服务说明》</span>
              </label>
            </div>
          </div>

          <!-- 提交 -->
          <div class="charge-row">
            <div class="charge-label"></div>
            <div class="charge-body">
              <button class="btn btn-primary pay-btn" type="button" @click="repay">
                <svg class="pay-lock" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                立即支付
              </button>
              <p class="pay-err" v-if="payErr">{{ payErr }}</p>
            </div>
          </div>
        </div>

        <!-- 计费口径提示 -->
        <div class="charge-note">
          <div>
            <b>充值余额 = 按次调用</b>
            <span>余额通用、随充随用，适合低频灵活调用。包年 / 包天套餐请在 API 数据平台选购。</span>
          </div>
          <router-link class="btn btn-ghost btn-sm" to="/api">查看计费方式</router-link>
        </div>
      </div>
    </section>

    <!-- 使用流程 -->
    <section class="band band-alt">
      <div class="container">
        <div class="band-head">
          <span class="band-num">FLOW</span>
          <h2>使用流程</h2>
          <span class="rule"></span>
        </div>
        <p class="band-lead">从认证到调用共 5 步，完成充值后即可申请接口并在数据产品页在线测试。</p>
        <div class="flow">
          <div class="flow-step">
            <span class="flow-ico">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v6c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6l7-3Z"/><path d="M9 12l2 2 4-4"/></svg>
            </span>
            <h4>1. 企业认证</h4>
            <p>认证企业<br>提供真实有效的信息</p>
          </div>
          <div class="flow-step">
            <span class="flow-ico">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="6" width="19" height="12.5" rx="2.5"/><path d="M2.5 10.5h19"/><path d="M6 15h4"/></svg>
            </span>
            <h4>2. 账户充值</h4>
            <p>充值成功<br>账户余额到账</p>
          </div>
          <div class="flow-step">
            <span class="flow-ico">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z"/><path d="M14 3v5h5"/><path d="M12 12v5M9.5 14.5h5"/></svg>
            </span>
            <h4>3. 申请接口</h4>
            <p>个人中心 → 接口列表<br>申请需要接口</p>
          </div>
          <div class="flow-step">
            <span class="flow-ico">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12Z"/><circle cx="12" cy="12" r="2.8"/></svg>
            </span>
            <h4>4. 查看接口</h4>
            <p>个人中心 → 接口列表<br>查看已申请的接口</p>
          </div>
          <div class="flow-step">
            <span class="flow-ico">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M9 7l-5 5 5 5"/><path d="M15 7l5 5-5 5"/></svg>
            </span>
            <h4>5. 调用接口</h4>
            <p>测试接口或调用接口<br>查看用量与明细</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="section" style="padding:0 0 96px;">
      <div class="container">
        <div class="cta cta-photo">
          <h2>充值前想先验证数据质量？</h2>
          <p>200+ 标准/可定制化接口提供完整文档与在线测试，登录后即可使用免费调试额度核对字段与命中率。</p>
          <div class="cta-btns">
            <router-link class="btn" to="/api">在线接口测试</router-link>
            <router-link class="btn btn-outline-light" to="/api">进入数据产品 →</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- 扫码支付弹窗（微信 / 支付宝，仅在支付方式为 2 / 1 且下单成功后出现） -->
    <div class="pay-modal" v-show="dialogFormVisible">
      <div class="pay-modal-card">
        <button class="pay-modal-close" type="button" aria-label="关闭" @click="closeWecat">×</button>
        <h3 class="pay-modal-title">{{ form.type===2?'微信':'支付宝' }}支付</h3>
        <div class="pay-modal-body">
          <div class="pay-qr">
            <div class="qrcode" ref="qrCodeUrl" v-if="codeHtml"></div>
          </div>
          <div class="pay-modal-info">
            <p class="pay-scan-hint">请用{{ form.type===2?'微信':'支付宝' }}扫一扫，扫描二维码支付</p>
            <p>充值金额：<b>{{ wechat1 }}</b></p>
            <p>订单编号：<span class="mono">{{ wechat2 }}</span></p>
            <p>实付金额：<b class="hl">{{ wechat3 }} 元</b></p>
            <p class="pay-modal-timer">请在 <b>{{ daojishi }}</b> 内完成支付</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 支付结果 -->
    <el-dialog
      :visible.sync="resultVisible"
      :close-on-click-modal="false"
      :before-close="handleClose"
      width="644px">
      <div class="pay-result" :class="chargeState">
        <div class="pay-result-head">
          <span class="pay-result-ico" v-html="chargeState==='success'?okIcon:failIcon"></span>
          <b>{{ chargeState==='success'?'恭喜您充值成功！':'充值失败！' }}</b>
        </div>
        <div class="pay-ok-grid six" v-if="chargeState==='success'">
          <div><span>订单号</span><b>{{ resultFields.orderNo }}</b></div>
          <div><span>支付方式</span><b>{{ resultFields.method }}</b></div>
          <div><span>实付金额</span><b>{{ resultFields.paid }}</b></div>
          <div><span>赠送金额</span><b>{{ resultFields.bonus }}</b></div>
          <div class="hl"><span>本次到账</span><b>{{ resultFields.total }}</b></div>
          <div><span>账户余额</span><b>{{ resultFields.balance }}</b></div>
        </div>
        <p class="pay-ok-note" v-if="chargeState==='success' && !balanceKnown">
          余额以服务端记录为准，最新余额与充值记录可在
          <router-link to="/center/case1">个人中心 · 控制台</router-link> 查看。
        </p>
        <p class="pay-ok-note" v-if="chargeState==='fail'">
          支付未完成或已取消。如已扣款但未到账，请稍后在个人中心核对订单，或联系客服处理。
        </p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import secret from '@/components/secret.vue'
import QRCode from 'qrcodejs2'
import { userApi, payApi } from '@/api'

let timer = null
let chatTimer = null

/* 赠送规则：阈值与旧版 getGiveMoney 完全一致，抽成纯函数供档位卡片与计算共用，
   保证卡片上展示的赠送额与实际提交给后端的 giveAmount 同源。 */
function giveMoneyFor(money) {
  if (money >= 20000) return 5000
  if (money >= 10000) return 2500
  if (money >= 5000) return 1000
  if (money >= 2000) return 200
  if (money >= 500) return 50
  return 0
}

/* 金额档位：与旧版 radio 档位一一对应（金额、赠送、角标文案均沿用旧口径） */
const PLANS = [
  { amount: 500, tag: '优惠' },
  { amount: 2000, tag: '优惠' },
  { amount: 5000, tag: '优惠' },
  { amount: 10000, tag: '充值推荐', hot: true },
  { amount: 20000, tag: '优惠' }
]

const OK_ICON =
  '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9"></circle><path d="M8 12.5l2.6 2.6L16 9.7"></path></svg>'

const FAIL_ICON =
  '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9"></circle><path d="M15 9l-6 6M9 9l6 6"></path></svg>'

export default {
  name: 'charge',
  components: {
    secret
  },
  data() {
    return {
      // 档位与赠送规则（价格未登录也可见）
      plans: PLANS,
      form: {
        number: '',
        type: 0,
        money: ''
      },
      dialogFormVisible: false,
      checked: false,
      state: 0,
      resultVisible: false,
      giveAmount: undefined,
      chargeState: 'success',
      start: 3600,
      daojishi: '00:00:00',
      chargeOrderNum: '',
      codeHtml: true,
      wechat1: '',
      wechat2: '',
      wechat3: '',
      // 支付结果卡数据（来自 pay/amount/charge 返回）
      payResult: {
        orderNo: '',
        payType: 0,
        paidAmount: ''
      },
      payErr: '',
      okIcon: OK_ICON,
      failIcon: FAIL_ICON
    }
  },
  computed: {
    logged() {
      const user = this.$cookies.getJSON('user')
      return !!(user && user.userId)
    },
    userName() {
      const user = this.$cookies.getJSON('user') || {}
      return user.userName || user.username || user.phone || '当前账号'
    },
    // 余额只在登录响应带回 accountBalance 时展示；不额外发请求（接口清单冻结）
    balanceKnown() {
      const user = this.$cookies.getJSON('user') || {}
      return user.accountBalance !== undefined && user.accountBalance !== null && user.accountBalance !== ''
    },
    balanceText() {
      const user = this.$cookies.getJSON('user') || {}
      const n = Number(user.accountBalance)
      return isNaN(n) ? String(user.accountBalance) : n.toLocaleString('zh-CN')
    },
    giftHint() {
      if (this.giveAmount > 0) {
        const paid = this.localAmount
        return '实付 ' + paid + ' 元，到账 ' + (paid + this.giveAmount) + ' 元'
      }
      return this.localAmount > 0 ? '自定义金额不参与赠送活动' : '选择上方档位即可获得赠送'
    },
    // 本地口径的实付金额（元）：档位单选或自定义金额
    localAmount() {
      if (this.form.number) return Number(this.form.number)
      const money = Number(this.form.money)
      return isNaN(money) ? 0 : money
    },
    resultFields() {
      const hasPaid = this.payResult.paidAmount !== '' && this.payResult.paidAmount !== null
      const paid = hasPaid ? Number(this.payResult.paidAmount) : this.localAmount
      const paidNumber = isNaN(paid) ? 0 : paid
      const bonus = Number(this.giveAmount || 0)
      const total = paidNumber + bonus
      return {
        orderNo: this.payResult.orderNo || '—',
        method: this.payResult.payType === 2 ? '微信支付' : (this.payResult.payType === 1 ? '支付宝' : '—'),
        paid: paidNumber ? paidNumber + ' 元' : '—',
        bonus: bonus + ' 元',
        total: total ? total + ' 元' : '—',
        balance: this.balanceKnown ? '¥' + this.balanceText : '见个人中心'
      }
    }
  },
  watch: {
    'form.number': function (newVal, oldVal) {
      if (newVal != 0) {
        this.form.money = ''
        this.getGiveMoney(newVal)
      }
    },
    'form.money': function (newVal, oldVal) {
      if (newVal != 0) {
        this.form.number = 0
        this.getGiveMoney(newVal)
      }
    }
  },
  beforeDestroy() {
    clearInterval(timer)
    clearInterval(chatTimer)
  },
  mounted() {
    this.getState()
    const res = this.$route.query.isSuccess
    if (res) {
      if (res === 'true' || res === true) {
        this.successCall()
      } else {
        this.failCall()
      }
    }
  },
  methods: {
    /* 档位卡片：驱动 form.number，后续计算与提交口径与旧版 radio 完全一致 */
    selectPlan(plan) {
      this.payErr = ''
      this.form.number = plan.amount
    },
    isPlanActive(plan) {
      return Number(this.form.number) === plan.amount
    },
    giveMoneyFor(money) {
      return giveMoneyFor(money)
    },
    openAuth(view) {
      window.dispatchEvent(new CustomEvent('dts:auth-open', {
        detail: { view: view || 'login' }
      }))
    },
    closeWecat() {
      clearInterval(timer)
      clearInterval(chatTimer)
      this.dialogFormVisible = false
      this.codeHtml = false
      let _this = this
      setTimeout(function () {
        _this.codeHtml = true
      }, 50)
    },
    getOrderId(val) {
      if (!val) return
      this.orderId = val
    },
    getGiveMoney(money) {
      this.giveAmount = giveMoneyFor(money)
    },
    backTime() {
      let _this = this
      clearInterval(timer)
      _this.start = 3600
      _this.solveTime(_this.start)
      timer = setInterval(function () {
        _this.start--
        _this.solveTime(_this.start)
      }, 1000)
    },
    solveTime(miao) {
      if (miao <= 1) {
        this.daojishi = '00:00:00'
        clearInterval(timer)
        return
      }
      let min = parseInt(miao / 60)
      let sec = miao % 60
      this.daojishi = `00:${min < 10 ? '0' + min : min}:${sec < 10 ? '0' + sec : sec}`
    },
    creatQrCode(url) {
      var qrcode = new QRCode(this.$refs.qrCodeUrl, {
        text: url,
        width: 132,
        height: 132,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.H
      })
    },
    repay() {
      this.payErr = ''
      let user = this.$cookies.getJSON('user')
      // 门禁：未登录只唤起登录弹窗，价格与已选档位都保留
      if (!(user && user.token)) {
        this.$store.commit('setShowLogin', 1)
        return
      }
      if (!this.form.number && !this.form.money) {
        this.payErr = '请选择充值金额'
        return
      }
      if (this.form.money < 0) {
        this.payErr = '请输入正确金额'
        return
      }
      if ((this.form.money > 0 && this.form.money < 100) || this.form.money % 1 != 0) {
        this.payErr = '最低自定义充值金额为100元，且金额为整数'
        return
      }
      if (this.form.money > 100000) {
        this.payErr = '自定义金额最大限额不超过100000元'
        return
      }
      if (!this.form.type) {
        this.payErr = '请选择充值方式'
        return
      }
      if (!this.checked) {
        this.payErr = '请阅读并勾选下方协议'
        return
      }
      this.submitMoney()
    },
    checkState(val) {
      let _this = this
      let params = {
        chargeOrderNo: val
      }
      chatTimer = setInterval(function () {
        payApi.getChargeOrderState(params)
          .then((res) => {
            if (res.state === 200) {
              if (res.data === 'SUCCESS') {
                clearInterval(chatTimer)
                _this.dialogFormVisible = false
                _this.$message.success('充值成功')
              }
            } else {
              _this.$message.error(res.message)
            }
          })
          .catch(err => {
            _this.$message.error(err)
          })
      }, 5000)
    },
    inputMoney() {
      this.form.number = 0
      this.giveAmount = undefined
    },
    submitMoney() {
      let user = this.$cookies.getJSON('user')
      if (!user || !user.userId) {
        this.$store.commit('setShowLogin', 1)
        return
      }
      if (this.state === 1) {
        this.$message.error('仅限完成企业认证的用户充值，即将为您跳转到认证页面')
        this.jump()
        return
      }
      if (this.state === 2) {
        this.$message.error('您已提交企业认证信息，正在审核中，请等待...')
        return
      }
      if (this.state === 3) {
        this.$message.error('您提交的企业认证信息，审核未通过')
        return
      }
      let repayType = parseInt(this.form.type)
      let charge = 0
      if (this.form.number != 100) {
        charge = this.form.number ? this.form.number * 100 : this.form.money * 100
      }
      let give = this.giveAmount ? this.giveAmount * 100 : 0
      let params = {
        userId: this.$cookies.getJSON('user').userId,
        orderId: this.orderId,
        payType: repayType,
        chargeType: 1,
        amount: charge,
        giveAmount: give
      }
      payApi.charge(params)
        .then((res) => {
          if (res.state === 200) {
            // 记录结果卡数据（订单号 / 支付方式 / 实付金额），字段与扫码弹窗同源
            this.payResult.orderNo = res.data.chargeOrderNo || ''
            this.payResult.payType = repayType
            this.payResult.paidAmount = (res.data.paidAmount === undefined || res.data.paidAmount === null)
              ? ''
              : res.data.paidAmount
            if (res.data.url === 'success') {
              this.successCall()
            } else if (res.data.url === 'fail') {
              this.failCall()
            } else {
              if (repayType === 1) {
                const newWindow = window.open('', '_blank')
                newWindow.document.write(res.data.url)
                newWindow.focus()
              } else if (repayType === 2) {
                this.creatQrCode(res.data.url)
                this.wechat1 = res.data.chargeOption
                this.wechat2 = res.data.chargeOrderNo
                this.wechat3 = res.data.paidAmount
                this.dialogFormVisible = true
                this.backTime()
                this.checkState(res.data.chargeOrderNo)
              }
            }
          } else {
            this.$message.error(res.message)
          }
        })
        .catch(err => {
          this.$message.error(err)
        })
    },
    goOrg() {
      if (!this.$cookies.getJSON('user').userId) {
        this.$store.commit('setShowLogin', 1)
      } else {
        this.$router.push('/center/case2')
      }
    },
    handleClose(done) {
      this.$store.commit('setOtherMeng', 0)
      done()
    },
    getState() {
      // 旧实现在未登录时对 undefined 取 userId 会抛错，导致 mounted 后半段（isSuccess 处理）不执行；
      // 这里补上空值判断，请求条件与旧版一致（仅登录用户请求 user/org/cert/state）
      const user = this.$cookies.getJSON('user')
      if (!user || !user.userId) return
      let params = {
        userId: user.userId
      }
      userApi.getOrgCertState(params)
        .then((res) => {
          if (res.state === 200) {
            this.state = res.data
          } else if (res.state === 400) {
            this.state = 1
          } else {
            this.$message.error(res.message)
          }
          if (this.state === 5) {
            this.jump()
          }
        })
        .catch(err => {
          this.$message.error(err)
        })
    },
    jump() {
      let _this = this
      setTimeout(function () {
        _this.$router.push('/center/case2')
      }, 3000)
    },
    successCall() {
      this.$store.commit('setOtherMeng', 1)
      this.resultVisible = true
      this.chargeState = 'success'
    },
    failCall() {
      this.$store.commit('setOtherMeng', 1)
      this.resultVisible = true
      this.chargeState = 'fail'
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/charge/index.vue